import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import FocusTrap from 'components/FocusTrap';

import styles from './ProductSelectMobileScreen.scss';

export interface ProductSelectMobileScreenProps {
  isOpen: boolean;
  titleText: string;
  children: React.ReactNode;
  onClickBack: () => void;
  onDismiss: () => void;
  mobileHeader?: React.ReactElement<any> | null;
}

const lockBody = (val: boolean = true) => {
  document.querySelector('body')?.style.setProperty('overflow', val ? 'hidden' : '');
};

const ProductSelectMobileScreen = ({
  isOpen,
  titleText,
  onClickBack,
  onDismiss,
  children,
  mobileHeader = null,
}: ProductSelectMobileScreenProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const backLinkRef = useRef<HTMLButtonElement>(null);
  const [active, setActive] = useState(false);
  const [open, setOpen] = useState(false);

  const dismiss = useCallback(() => {
    setActive(false);
    lockBody(false);
    onDismiss();
  }, [onDismiss]);

  useEffect(() => {
    if (isOpen) {
      setActive(true);
      lockBody(true);

      const timerId = setTimeout(() => setOpen(true));

      return () => {
        clearTimeout(timerId);
      };
    }

    const screenRef = ref.current;

    if (screenRef) {
      setOpen(false);

      screenRef.addEventListener('transitionend', dismiss, { once: true });

      return () => {
        screenRef.removeEventListener('transitionend', dismiss);
      };
    }

    return () => {};
  }, [dismiss, isOpen]);

  useEffect(() => () => lockBody(false), []);

  return (
    <FocusTrap active={active}>
      <div
        data-testid="product-select-mobile-screen"
        className={classNames(styles.screen, {
          [styles.open]: open,
          [styles.active]: active,
        })}
        ref={ref}
      >
        <header className={styles.topbar}>
          <div className={styles.headerTop}>
            <TextLink
              className={styles.backButton}
              iconLeft
              underline="never"
              component="button"
              onClick={onClickBack}
              ref={backLinkRef}
            >
              Back
            </TextLink>
            <Typography styleAs="paragraphHeading" element="h2" noMargins>
              {titleText}
            </Typography>
          </div>
          {mobileHeader}
        </header>
        <div className={styles.content}>{children}</div>
      </div>
    </FocusTrap>
  );
};

ProductSelectMobileScreen.displayName = 'ProductSelectMobileScreen';

export default ProductSelectMobileScreen;
